// AlignmentFormPage.tsx
// The player half of the Alignment Board. Spec: md/CLASS_ALIGNMENT_PLAN.md
//
// An officer posts /a/<token> in Discord. A player opens it, signs in with
// Discord, and lists every class + spec they would be willing to play in WoW
// Forever, in preference order, with the professions they would take on each.
//
// WHY SIGN-IN: the whole tool exists so officers stop asking the same human to
// play two different classes without realising it is the same human. Signing in
// means the board is keyed on a real Discord id, so a second submission edits
// the first instead of creating a twin. It is not a request for access to
// Rallify, which is why these routes sit in WAITLIST_ALLOW.
//
// ROLE IS NEVER ASKED FOR. Every spec already knows its own role, so the form
// collects class + spec and the officer's composition readout derives the rest.
//
// LOOK AND FEEL. This page is the first thing most players ever see of the
// tool, over the guild's own artwork, so it is designed rather than assembled:
// Inter throughout with heavier, tighter headings,
// glass panels over the art, real spec and class icons, and one primary action
// per screen. Both fonts are applied inline and scoped to this page, so nothing
// else in the app changes typeface.
import React, { useCallback, useEffect, useState } from "react";
import { API_URL } from "./config";
import { ThemeProvider, useTheme } from "./theme/ThemeProvider";
import { ThemeToggle } from "./theme/ThemedPage";
import { getZamClassIconUrl } from "./modalChrome";
import { BODY, CHOICE_COLOR, CoverageSidebar, DISPLAY, GhostButton, Glass, Icon, PrimaryButton, RolePill, tc, useAlignmentTheme } from "./alignmentUi";
import { buildClasses, buildRoles, PoolStats } from "./alignmentStats";

type CatalogItem = {
  item_key: string;
  label: string;
  parent_key: string | null;
  role_key: string | null;
  color: string | null;
  image_url?: string | null;
  metadata: { is_secondary?: boolean; is_gathering?: boolean } | null;
};

type Catalog = {
  classes: CatalogItem[];
  specs: CatalogItem[];
  roles: CatalogItem[];
  professions: CatalogItem[];
  factions: CatalogItem[];
  realmTypes: CatalogItem[];
};

type Combo = { class_key: string; spec_key: string; professions: string[] };

type Props = {
  token: string;
  me: { id: string; display_name?: string; username?: string } | null;
  onDone?: () => void;
};

// ---------------------------------------------------------------------------
// Furniture
// ---------------------------------------------------------------------------

function Section({
  step, title, subtitle, right, children,
}: {
  step?: number;
  title: string;
  subtitle?: string;
  right?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Glass className="p-5 sm:p-7">
      <div className="mb-5 flex items-start gap-4">
        {step != null && (
          <span
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-amber-400/40 bg-gradient-to-b from-amber-400/25 to-amber-600/10 text-lg font-bold text-amber-800 dark:text-amber-200"
            style={DISPLAY}
          >
            {step}
          </span>
        )}
        <div className="min-w-0 flex-1">
          <h2 className="text-xl font-bold tracking-wide text-[var(--c-ink)] sm:text-2xl" style={DISPLAY}>
            {title}
          </h2>
          {subtitle && <p className="mt-1 text-[15px] leading-relaxed text-[var(--c-ink-2)]">{subtitle}</p>}
        </div>
        {right}
      </div>
      {children}
    </Glass>
  );
}

// Sets expectations: a sign up is input to the officers, not a vote. A guild's
// own note (set by its officers on the board) replaces this general wording,
// which is worded to fit any guild.
const INPUT_NOT_A_VOTE =
  "We value your input. Your answers help your officers plan, and final roster decisions are made based on what best serves the guild.";

function Hero({ guildName, title }: { guildName?: string; title: string }) {
  return (
    <div className="pb-8 pt-10 sm:pb-10 sm:pt-20">
      {guildName && (
        <div className="text-xs font-bold uppercase tracking-[0.3em] text-[var(--c-accent-ink)] dark:drop-shadow-[0_2px_8px_rgba(0,0,0,0.9)]">
          {guildName}
        </div>
      )}
      <h1
        // The shadow lifts white type off busy artwork; on the light palette the
        // artwork is washed out behind dark type and needs none.
        className="mt-2 text-4xl font-bold leading-tight text-[var(--c-ink)] sm:text-6xl dark:[text-shadow:0_4px_30px_rgba(0,0,0,0.85)]"
        style={DISPLAY}
      >
        {title}
      </h1>
      <div className="mt-4 h-px w-28 bg-gradient-to-r from-[var(--c-accent)] to-transparent" />
    </div>
  );
}

// Big tiles for the two once-per-person questions. One tap on a phone, and the
// answer is visible without opening anything.
function BigChoice({
  label, options, value, onChange,
}: {
  label: string;
  options: CatalogItem[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <div className="mb-2 text-sm font-semibold text-[var(--c-ink-2)]">{label}</div>
      <div className="grid grid-cols-3 gap-2">
        {options.map((o) => {
          const on = value === o.item_key;
          const c = CHOICE_COLOR[o.item_key] || o.color || "#F59E0B";
          return (
            <button
              key={o.item_key}
              type="button"
              onClick={() => onChange(o.item_key)}
              className={`relative rounded-2xl border px-2 py-4 text-center transition ${
                on ? "" : "border-[var(--c-line)] bg-[var(--c-sunken)] text-[var(--c-ink-2)] hover:border-[var(--c-line-strong)] hover:bg-[var(--c-sunken)]"
              }`}
              style={
                on
                  ? {
                      borderColor: c,
                      color: c,
                      background: `radial-gradient(120% 120% at 50% 0%, ${c}33 0%, ${c}0d 60%, transparent 100%)`,
                      boxShadow: `0 0 0 1px ${c}, 0 10px 30px -12px ${c}`,
                    }
                  : undefined
              }
            >
              {on && (
                <span
                  className="absolute right-2 top-2 flex h-4 w-4 items-center justify-center rounded-full text-[10px] font-black text-black"
                  style={{ background: c }}
                >
                  ✓
                </span>
              )}
              <span className="text-[15px] font-bold">{o.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

// The sign up form is its own full page (a share link, outside the app
// shell), so it carries its own theme provider and light/dark switch.
export default function AlignmentFormPage(props: Props) {
  return (
    <ThemeProvider>
      <AlignmentForm {...props} />
    </ThemeProvider>
  );
}

function AlignmentForm({ token, me, onDone }: Props) {
  useAlignmentTheme();
  const { themeClass } = useTheme();
  const [intro, setIntro] = useState<
    { boardName: string; guildName: string; heroImageUrl?: string | null; signupNote?: string | null } | null
  >(null);
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  // Bare counts for the sidebar. Arrives with the form itself, so the panel is
  // never a second spinner next to the thing somebody came here to fill in.
  const [stats, setStats] = useState<PoolStats | null>(null);
  const [maxPrimary, setMaxPrimary] = useState(2);
  const [combos, setCombos] = useState<Combo[]>([]);
  const [displayName, setDisplayName] = useState("");
  const [note, setNote] = useState("");
  const [openClass, setOpenClass] = useState<string | null>(null);
  // Professions are edited one row at a time. Rendering the full grid under
  // every pick was most of the visual noise on the old page.
  const [openProf, setOpenProf] = useState<string | null>(null);
  const [unclaimed, setUnclaimed] = useState<{ id: string; display_name: string } | null>(null);
  const [suggestedNames, setSuggestedNames] = useState<string[]>([]);
  const [faction, setFaction] = useState("");
  const [realmType, setRealmType] = useState("");
  const [openToAnything, setOpenToAnything] = useState(false);
  // Returning players skip straight to their answers; only a first visit
  // gets the explainer, because reading it twice is just a click in the way.
  const [step, setStep] = useState<"intro" | "form">("intro");
  const signupNote = intro?.signupNote?.trim() || INPUT_NOT_A_VOTE;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [closed, setClosed] = useState(false);

  // Pre sign-in. Returns the guild and board name and nothing else, because
  // anyone can hold this link.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/alignment/${encodeURIComponent(token)}`, {
          credentials: "include",
        });
        if (res.status === 410) { if (alive) setClosed(true); return; }
        if (!res.ok) throw new Error("not_found");
        const data = await res.json();
        if (alive) setIntro(data);
      } catch {
        if (alive) setError("That link is not valid any more.");
      } finally {
        if (alive && !me) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [token, me]);

  // Post sign-in: the catalogue to build the form from, plus anything this
  // Discord account already submitted so the form comes back filled in.
  useEffect(() => {
    if (!me) return;
    // The bounce is done: drop the stashed token so a later visit to the site
    // root does not silently reopen this form instead of the dashboard. The
    // component's own state keeps it alive for the rest of this visit.
    sessionStorage.removeItem("pending_alignment_token");
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/alignment/${encodeURIComponent(token)}/me`, {
          credentials: "include",
        });
        if (res.status === 410) { if (alive) setClosed(true); return; }
        if (!res.ok) throw new Error("load_failed");
        const data = await res.json();
        if (!alive) return;
        setCatalog(data.catalog);
        setStats(data.stats || null);
        setMaxPrimary(data.maxPrimaryProfessions ?? 2);
        setUnclaimed(data.unclaimed || null);
        setSuggestedNames(data.suggestedNames || []);
        if (data.me) {
          setDisplayName(data.me.display_name || "");
          setNote(data.me.note || "");
          setFaction(data.me.faction || "");
          setRealmType(data.me.realm_type || "");
          setOpenToAnything(!!data.me.open_to_anything);
          setStep("form");
          setCombos((data.me.combos || []).map((c: any) => ({
            class_key: c.class_key,
            spec_key: c.spec_key,
            professions: c.professions || [],
          })));
        } else {
          // Prefill from a character they already have in this guild, NOT from
          // their Discord handle. Officers know people by their in game name,
          // and quietly filing someone under a Discord name is how a roster
          // fills up with strangers.
          setDisplayName(data.suggestedNames?.[0] || "");
        }
      } catch {
        if (alive) setError("Could not load the form. Try again in a moment.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [me, token]);

  const handleLogin = () => {
    // The Discord callback returns to the site root, so the token has to
    // survive the bounce in sessionStorage. Same pattern as the invite and
    // join-request links.
    sessionStorage.setItem("pending_alignment_token", token);
    window.location.href = `${API_URL}/auth/discord`;
  };

  const specsFor = useCallback(
    (classKey: string) => (catalog?.specs || []).filter((s) => s.parent_key === classKey),
    [catalog]
  );
  const roleLabel = useCallback(
    (roleKey: string | null) =>
      (catalog?.roles || []).find((r) => r.item_key === roleKey)?.label || "",
    [catalog]
  );
  const specOf = useCallback(
    (specKey: string) => (catalog?.specs || []).find((s) => s.item_key === specKey),
    [catalog]
  );
  const classOf = useCallback(
    (classKey: string) => (catalog?.classes || []).find((c) => c.item_key === classKey),
    [catalog]
  );
  const classIcon = (cls: CatalogItem | undefined) =>
    cls?.image_url || getZamClassIconUrl(cls?.label);
  const specIcon = (spec: CatalogItem | undefined, cls: CatalogItem | undefined) =>
    spec?.image_url || classIcon(cls);

  const toggleSpec = (classKey: string, specKey: string) => {
    setCombos((prev) => {
      const at = prev.findIndex((c) => c.spec_key === specKey);
      if (at >= 0) return prev.filter((_, i) => i !== at);
      // New combos append to the bottom. Rank is preference order, and a player
      // adding a fallback should not have it silently outrank their main.
      return [...prev, { class_key: classKey, spec_key: specKey, professions: [] }];
    });
  };

  // Tick every spec of a class in one action. Devin's ask: "important that they
  // can pick multiple specs, so you can in theory pick em all".
  const toggleWholeClass = (classKey: string) => {
    const all = specsFor(classKey).map((s) => s.item_key);
    setCombos((prev) => {
      const has = all.every((k) => prev.some((c) => c.spec_key === k));
      if (has) return prev.filter((c) => !all.includes(c.spec_key));
      const missing = all.filter((k) => !prev.some((c) => c.spec_key === k));
      return [...prev, ...missing.map((k) => ({ class_key: classKey, spec_key: k, professions: [] }))];
    });
  };

  const move = (index: number, by: number) => {
    setCombos((prev) => {
      const to = index + by;
      if (to < 0 || to >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[to]] = [next[to], next[index]];
      return next;
    });
  };

  const toggleProfession = (index: number, profKey: string) => {
    setCombos((prev) => prev.map((c, i) => {
      if (i !== index) return c;
      const has = c.professions.includes(profKey);
      if (has) return { ...c, professions: c.professions.filter((p) => p !== profKey) };
      const prof = (catalog?.professions || []).find((p) => p.item_key === profKey);
      if (!prof?.metadata?.is_secondary) {
        const primaries = c.professions.filter((p) => {
          const m = (catalog?.professions || []).find((x) => x.item_key === p);
          return !m?.metadata?.is_secondary;
        }).length;
        if (primaries >= maxPrimary) return c;
      }
      return { ...c, professions: [...c.professions, profKey] };
    }));
  };

  const claim = async () => {
    if (!unclaimed) return;
    await fetch(`${API_URL}/api/alignment/${encodeURIComponent(token)}/claim`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ personId: unclaimed.id }),
    });
    setUnclaimed(null);
  };

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/alignment/${encodeURIComponent(token)}/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ displayName, note, combos, faction, realmType, openToAnything }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Could not save.");
      if (data.stats) setStats(data.stats);
      setSaved(true);
    } catch (e: any) {
      setError(e.message || "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  // Full bleed, fixed, so the art stays put while the form scrolls over it.
  // Three stacked layers, and each earns its place: a solid base in case the
  // image is slow or missing, a flat scrim to drag the whole thing dark enough
  // for white text, and a vertical gradient so the top of the picture survives
  // while the bottom fades into the page. Snow and sky are bright, and a single
  // light overlay leaves body copy unreadable over the pale half of any frame.
  const bg = intro?.heroImageUrl;
  const shell = (children: React.ReactNode, aside?: React.ReactNode) => (
    <div className={`${themeClass} relative min-h-screen bg-[var(--c-bg)] text-[var(--c-ink)] antialiased`} style={BODY}>
      <div className="fixed right-4 top-4 z-30"><ThemeToggle /></div>
      <div className="fixed inset-0 z-0" aria-hidden="true">
        {bg ? (
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: `url(${bg})` }}
          />
        ) : (
          <div
            className="absolute inset-0"
            style={{ background: "radial-gradient(120% 80% at 50% 0%, var(--c-hero-1) 0%, var(--c-hero-2) 45%, var(--c-bg) 100%)" }}
          />
        )}
        <div className="absolute inset-0 bg-[color-mix(in_srgb,var(--c-bg)_60%,transparent)]" />
        <div className="absolute inset-0 bg-gradient-to-b from-[color-mix(in_srgb,var(--c-bg)_10%,transparent)] via-[color-mix(in_srgb,var(--c-bg)_65%,transparent)] to-[var(--c-bg)]" />
      </div>
      {/* One column until there is room for two. The form keeps its own
          max-w-3xl measure either way, so nothing about it narrows to make
          space: the sidebar is added beside it, never taken out of it. Below
          lg the panel falls underneath, which is the right order on a phone
          because the form is what somebody opened the link to do. */}
      <div className={`relative z-10 mx-auto px-4 pb-24 ${aside ? "max-w-6xl" : "max-w-3xl"}`}>
        {aside ? (
          <div className="gap-7 lg:grid lg:grid-cols-[minmax(0,1fr)_296px] lg:items-start">
            <div className="min-w-0 lg:max-w-3xl">{children}</div>
            <aside className="mt-8 lg:sticky lg:top-6 lg:mt-24">{aside}</aside>
          </div>
        ) : (
          children
        )}
      </div>
    </div>
  );

  // Counts only, never people, and only after sign-in: the same boundary the
  // anonymous link already had. Shown BEFORE the form because it is what a
  // player is weighing up while they decide what to roll, and again on the
  // thank you page, by then including the answer they just gave.
  const factionLabel = (k: string) =>
    (catalog?.factions || []).find((f) => f.item_key === k)?.label || k;
  const realmLabel = (k: string) =>
    (catalog?.realmTypes || []).find((r) => r.item_key === k)?.label || k;
  const sidebar = stats && catalog ? (
    <CoverageSidebar
      registered={stats.registered}
      roles={buildRoles(catalog, stats)}
      classes={buildClasses(catalog, stats)}
      splits={[
        {
          title: "side",
          left: { label: factionLabel("alliance"), n: stats.factions.alliance || 0, color: CHOICE_COLOR.alliance },
          right: { label: factionLabel("horde"), n: stats.factions.horde || 0, color: CHOICE_COLOR.horde },
          middle: { label: "No preference", n: stats.factions.either || 0 },
        },
        {
          title: "realm",
          left: { label: realmLabel("pve"), n: stats.realmTypes.pve || 0, color: CHOICE_COLOR.pve },
          right: { label: realmLabel("pvp"), n: stats.realmTypes.pvp || 0, color: CHOICE_COLOR.pvp },
          middle: { label: "No preference", n: stats.realmTypes.either || 0 },
        },
      ]}
    />
  ) : null;

  if (closed) {
    return shell(
      <>
        <Hero guildName={intro?.guildName} title={intro?.boardName || "WoW Forever"} />
        <Glass className="p-8 text-center">
          <h2 className="text-2xl font-bold text-[var(--c-ink)]" style={DISPLAY}>Sign ups are closed</h2>
          <p className="mx-auto mt-2 max-w-md text-[15px] text-[var(--c-ink-2)]">
            Your officers have stopped taking answers on this link for now.
          </p>
        </Glass>
      </>
    );
  }

  if (loading) {
    return shell(
      <div className="flex min-h-[70vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-[var(--c-line-strong)] border-t-amber-400" />
      </div>
    );
  }

  // Signed out: name the guild so the player knows what they clicked, then one
  // button. Nothing about the roster is shown before sign-in.
  if (!me) {
    return shell(
      <>
        <Hero guildName={intro?.guildName} title={intro?.boardName || "WoW Forever"} />
        <Glass className="p-8 sm:p-10">
          <p className="max-w-lg text-lg leading-relaxed text-[var(--c-ink)]">
            Tell your officers which classes and specs you would be happy to play,
            and the professions you would level. It takes about two minutes.
          </p>
          {error && <div className="mt-4 text-sm text-rose-700 dark:text-rose-300">{error}</div>}
          <PrimaryButton onClick={handleLogin} className="mt-7">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M20.3 4.4A19.6 19.6 0 0 0 15.4 3l-.6 1.2a18 18 0 0 0-5.6 0L8.6 3a19.6 19.6 0 0 0-4.9 1.4C.6 9 0 13.5.3 18a19.8 19.8 0 0 0 6 3l1.3-2a12.8 12.8 0 0 1-2-1l.5-.4a14 14 0 0 0 11.8 0l.5.4a12.8 12.8 0 0 1-2 1l1.3 2a19.7 19.7 0 0 0 6-3c.4-5.2-.7-9.7-3.4-13.6ZM8.5 15.3c-1.2 0-2.1-1.1-2.1-2.4s.9-2.4 2.1-2.4 2.1 1.1 2.1 2.4-1 2.4-2.1 2.4Zm7 0c-1.2 0-2.1-1.1-2.1-2.4s.9-2.4 2.1-2.4 2.1 1.1 2.1 2.4-.9 2.4-2.1 2.4Z" />
            </svg>
            Sign in with Discord
          </PrimaryButton>
          <p className="mt-4 text-sm text-[var(--c-ink-3)]">
            Signing in just proves who you are, so your answers stay yours and you
            can change them later.
          </p>
        </Glass>
      </>
    );
  }

  if (saved) {
    return shell(
      <>
        <Hero guildName={intro?.guildName} title={intro?.boardName || "WoW Forever"} />
        <Glass className="p-8 text-center sm:p-12">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full border border-emerald-400/40 bg-emerald-400/15 text-3xl text-emerald-700 dark:text-emerald-300 shadow-[0_0_40px_-10px_rgba(52,211,153,0.8)]">
            ✓
          </div>
          <h2 className="mt-5 text-3xl font-bold text-[var(--c-ink)]" style={DISPLAY}>You are on the list</h2>
          <p className="mx-auto mt-3 max-w-md text-[15px] leading-relaxed text-[var(--c-ink-2)]">
            Your officers can see your answers now. Come back to this same link any
            time to change them.
          </p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <GhostButton onClick={() => setSaved(false)}>Edit my answers</GhostButton>
            {onDone && <PrimaryButton onClick={onDone}>Done</PrimaryButton>}
          </div>
        </Glass>
      </>,
      sidebar
    );
  }

  if (step === "intro") {
    const steps: [string, string][] = [
      ["Say who you are", "Your in game name, which side you are rolling, and whether you want a PvP or PvE realm. No preference is a perfectly good answer."],
      ["List what you would play", "Every class and spec you would be happy on, plus the professions you would level. Pick as many as you like."],
      ["Put them in order", "Your favourite at the top. Officers ask about the top of your list first, so be honest about what you actually want."],
      ["Change it whenever", "Come back to this link any time. Your answers are saved to your Discord account, so nothing gets lost or doubled up."],
    ];
    return shell(
      <>
        <Hero guildName={intro?.guildName} title={intro?.boardName || "WoW Forever"} />
        <Glass className="p-6 sm:p-9">
          <h2 className="text-2xl font-bold text-[var(--c-ink)] sm:text-3xl" style={DISPLAY}>How this works</h2>
          <ol className="mt-7 grid gap-6 sm:grid-cols-2">
            {steps.map(([title, body], i) => (
              <li key={title} className="flex gap-4">
                <span
                  className="text-4xl font-bold leading-none text-amber-700/80 dark:text-amber-300/80"
                  style={DISPLAY}
                >
                  {i + 1}
                </span>
                <div>
                  <div className="text-[16px] font-semibold text-[var(--c-ink)]">{title}</div>
                  <p className="mt-1 text-[15px] leading-relaxed text-[var(--c-ink-2)]">{body}</p>
                </div>
              </li>
            ))}
          </ol>
          <p className="mt-8 whitespace-pre-line rounded-2xl border border-amber-400/30 bg-amber-400/10 px-5 py-4 text-[15px] leading-relaxed text-[var(--c-ink-2)]">
            {signupNote}
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-4 border-t border-[var(--c-line)] pt-6">
            <PrimaryButton onClick={() => setStep("form")}>Let us get started</PrimaryButton>
            <span className="text-sm text-[var(--c-ink-3)]">
              Nothing here commits you. It just shows what is possible.
            </span>
          </div>
        </Glass>
      </>,
      sidebar
    );
  }

  const professions = catalog?.professions || [];
  const primaries = professions.filter((p) => !p.metadata?.is_secondary);
  const secondaries = professions.filter((p) => p.metadata?.is_secondary);
  const profLabel = (k: string) => professions.find((p) => p.item_key === k)?.label || k;
  const choiceLabel = (list: CatalogItem[], k: string) => list.find((x) => x.item_key === k)?.label || k;

  // Say what is missing instead of greying a button out for no stated reason.
  const missing: string[] = [];
  if (!displayName.trim()) missing.push("your name");
  if (!faction) missing.push("a side");
  if (!realmType) missing.push("a realm type");
  if (combos.length === 0 && !openToAnything) missing.push("at least one spec");

  const openCls = openClass ? classOf(openClass) : undefined;

  return shell(
    <>
      <Hero guildName={intro?.guildName} title={intro?.boardName || "WoW Forever"} />

      <div className="space-y-5">
        {unclaimed && (
          <Glass className="border-amber-400/30 p-5">
            <div className="flex flex-wrap items-center gap-4">
              <p className="flex-1 text-[15px] text-amber-800 dark:text-amber-100">
                An officer already added <b>{unclaimed.display_name}</b> by hand. Is that you?
              </p>
              <PrimaryButton onClick={claim} className="px-4 py-2.5 text-sm">Yes, that is me</PrimaryButton>
            </div>
          </Glass>
        )}

        {/* 1. Who you are ------------------------------------------------ */}
        <Section step={1} title="Who you are" subtitle="Asked once, not per character.">
          <label className="block">
            <span className="mb-2 block text-sm font-semibold text-[var(--c-ink-2)]">In game name</span>
            <input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="The name your guild knows you by"
              className="w-full rounded-2xl border border-[var(--c-line)] bg-[var(--c-surface)] px-4 py-3.5 text-[16px] text-[var(--c-ink)] outline-none transition placeholder:text-[var(--c-ink-4)] focus:border-amber-400/60 focus:ring-4 focus:ring-amber-500/10"
            />
          </label>
          {suggestedNames.length > 0 && (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="text-sm text-[var(--c-ink-3)]">Your characters</span>
              {suggestedNames.map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setDisplayName(n)}
                  className={`rounded-full border px-3 py-1 text-sm font-medium transition ${
                    displayName === n
                      ? "border-amber-400/70 bg-amber-400/15 text-amber-800 dark:text-amber-100"
                      : "border-[var(--c-line-strong)] bg-[var(--c-sunken)] text-[var(--c-ink-2)] hover:bg-[var(--c-sunken-2)]"
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
          )}

          <div className="mt-7 grid gap-6 sm:grid-cols-2">
            <BigChoice
              label="Which side?"
              options={catalog?.factions || []}
              value={faction}
              onChange={setFaction}
            />
            <BigChoice
              label="Which realm type?"
              options={catalog?.realmTypes || []}
              value={realmType}
              onChange={setRealmType}
            />
          </div>
        </Section>

        {/* 2. What you would play ------------------------------------------ */}
        <Section
          step={2}
          title="What you would play"
          subtitle="Pick a class, then every spec you would be happy on."
        >
          <button
            type="button"
            onClick={() => setOpenToAnything((v) => !v)}
            className={`mb-5 flex w-full items-center gap-4 rounded-2xl border p-4 text-left transition ${
              openToAnything
                ? "border-amber-400/60 bg-gradient-to-r from-amber-400/15 to-transparent"
                : "border-[var(--c-line)] bg-[var(--c-bg-veil)] hover:bg-[var(--c-sunken)]"
            }`}
          >
            <span
              className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md border text-sm font-black transition ${
                openToAnything ? "border-[var(--c-accent)] bg-[var(--c-accent)] text-[var(--c-ink-on-fill)]" : "border-[var(--c-line-strong)]"
              }`}
            >
              {openToAnything ? "✓" : ""}
            </span>
            <span>
              <span className={`block text-[16px] font-semibold ${openToAnything ? "text-amber-800 dark:text-amber-100" : "text-[var(--c-ink)]"}`}>
                Tell me what to play
              </span>
              <span className="block text-sm text-[var(--c-ink-3)]">
                You do not mind. Your officers put you wherever the guild is short.
              </span>
            </span>
          </button>

          <div className="grid grid-cols-3 gap-2.5 sm:grid-cols-5">
            {(catalog?.classes || []).map((cls) => {
              const picked = combos.filter((c) => c.class_key === cls.item_key).length;
              const isOpen = openClass === cls.item_key;
              return (
                <button
                  key={cls.item_key}
                  type="button"
                  onClick={() => setOpenClass(isOpen ? null : cls.item_key)}
                  className={`group relative flex flex-col items-center gap-2 rounded-2xl border px-2 pb-3 pt-4 transition ${
                    isOpen
                      ? "border-amber-400/70 bg-amber-400/10"
                      : picked > 0
                        ? "border-[var(--c-line-strong)] bg-[var(--c-sunken)]"
                        : "border-[var(--c-line)] bg-[var(--c-bg-veil)] hover:border-[var(--c-line-strong)] hover:bg-[var(--c-sunken)]"
                  }`}
                >
                  {picked > 0 && (
                    <span className="absolute right-2 top-2 flex h-5 min-w-5 items-center justify-center rounded-full bg-[var(--c-accent)] px-1.5 text-[11px] font-black text-[var(--c-ink-on-fill)]">
                      {picked}
                    </span>
                  )}
                  <Icon
                    src={classIcon(cls)}
                    alt={cls.label}
                    size={44}
                    ring={picked > 0 || isOpen ? cls.color : null}
                  />
                  <span className="text-[13px] font-semibold" style={{ color: tc(cls.color || undefined) }}>
                    {cls.label}
                  </span>
                </button>
              );
            })}
          </div>

          {openClass && openCls && (
            <div className="mt-4 rounded-2xl border border-[var(--c-line)] bg-[var(--c-sunken)] p-4">
              <div className="mb-3 flex items-center gap-3">
                <Icon src={classIcon(openCls)} alt={openCls.label} size={28} />
                <span className="flex-1 text-[16px] font-semibold text-[var(--c-ink)]">
                  {openCls.label} specs
                </span>
                <GhostButton onClick={() => toggleWholeClass(openClass)} className="px-3 py-1.5 text-xs">
                  Pick all
                </GhostButton>
              </div>
              <div className="grid gap-2 sm:grid-cols-2">
                {specsFor(openClass).map((spec) => {
                  const on = combos.some((c) => c.spec_key === spec.item_key);
                  return (
                    <button
                      key={spec.item_key}
                      type="button"
                      onClick={() => toggleSpec(openClass, spec.item_key)}
                      className={`flex items-center gap-3 rounded-xl border p-2.5 text-left transition ${
                        on
                          ? "border-amber-400/60 bg-amber-400/10"
                          : "border-[var(--c-line)] bg-[var(--c-bg-veil)] hover:bg-[var(--c-sunken)]"
                      }`}
                    >
                      <Icon src={specIcon(spec, openCls)} alt={spec.label} size={36} ring={on ? "#fbbf24" : null} />
                      <span className="min-w-0 flex-1">
                        <span className="block text-[15px] font-semibold text-[var(--c-ink)]">{spec.label}</span>
                        <RolePill roleKey={spec.role_key} label={roleLabel(spec.role_key)} />
                      </span>
                      <span
                        className={`flex h-6 w-6 items-center justify-center rounded-full border text-xs font-black transition ${
                          on ? "border-[var(--c-accent)] bg-[var(--c-accent)] text-[var(--c-ink-on-fill)]" : "border-[var(--c-line-strong)] text-transparent"
                        }`}
                      >
                        ✓
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </Section>

        {/* 3. Order ---------------------------------------------------------- */}
        <Section
          step={3}
          title="Your order of preference"
          subtitle="Favourite at the top. Tap a pick to set its professions."
        >
          {combos.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-[var(--c-line-strong)] p-8 text-center text-[15px] text-[var(--c-ink-3)]">
              {openToAnything
                ? "Nothing listed, and that is fine. Your officers will choose for you."
                : "Nothing picked yet. Choose a class above to start."}
            </div>
          ) : (
            <ul className="space-y-2.5">
              {combos.map((combo, i) => {
                const spec = specOf(combo.spec_key);
                const cls = classOf(combo.class_key);
                const primaryCount = combo.professions.filter((p) => {
                  const m = professions.find((x) => x.item_key === p);
                  return !m?.metadata?.is_secondary;
                }).length;
                const expanded = openProf === combo.spec_key;
                return (
                  <li
                    key={combo.spec_key}
                    className={`overflow-hidden rounded-2xl border transition ${
                      expanded ? "border-amber-400/40 bg-[var(--c-sunken)]" : "border-[var(--c-line)] bg-[var(--c-bg-veil)]"
                    }`}
                  >
                    <div className="flex items-center gap-3 p-3">
                      <span
                        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold ${
                          i === 0
                            ? "bg-[var(--c-accent)] text-[var(--c-ink-on-fill)] dark:bg-gradient-to-b dark:from-amber-300 dark:to-amber-500 shadow-[0_0_20px_-4px_rgba(245,158,11,0.8)]"
                            : "border border-[var(--c-line-strong)] bg-[var(--c-sunken)] text-[var(--c-ink-2)]"
                        }`}
                        style={DISPLAY}
                      >
                        {i + 1}
                      </span>
                      <Icon src={specIcon(spec, cls)} alt={spec?.label || ""} size={42} ring={cls?.color} />
                      <button
                        type="button"
                        onClick={() => setOpenProf(expanded ? null : combo.spec_key)}
                        className="min-w-0 flex-1 text-left"
                      >
                        <span className="block truncate text-[16px] font-semibold" style={{ color: tc(cls?.color || undefined) }}>
                          {spec?.label} {cls?.label}
                        </span>
                        <span className="mt-1 flex flex-wrap items-center gap-1.5">
                          <RolePill roleKey={spec?.role_key || null} label={roleLabel(spec?.role_key || null)} />
                          {combo.professions.length > 0 ? (
                            combo.professions.map((p) => (
                              <span key={p} className="rounded-full bg-[var(--c-sunken)] px-2 py-0.5 text-[11px] font-medium text-[var(--c-ink-2)]">
                                {profLabel(p)}
                              </span>
                            ))
                          ) : (
                            <span className="text-xs font-medium text-amber-700/80 dark:text-amber-300/80">+ Add professions</span>
                          )}
                        </span>
                      </button>
                      <div className="flex shrink-0 items-center gap-1">
                        <button
                          type="button"
                          onClick={() => move(i, -1)}
                          disabled={i === 0}
                          className="flex h-8 w-8 items-center justify-center rounded-lg text-[var(--c-ink-2)] transition hover:bg-[var(--c-sunken-2)] hover:text-[var(--c-ink)] disabled:opacity-20"
                          aria-label="Move up"
                        >
                          ▲
                        </button>
                        <button
                          type="button"
                          onClick={() => move(i, 1)}
                          disabled={i === combos.length - 1}
                          className="flex h-8 w-8 items-center justify-center rounded-lg text-[var(--c-ink-2)] transition hover:bg-[var(--c-sunken-2)] hover:text-[var(--c-ink)] disabled:opacity-20"
                          aria-label="Move down"
                        >
                          ▼
                        </button>
                        <button
                          type="button"
                          onClick={() => toggleSpec(combo.class_key, combo.spec_key)}
                          className="flex h-8 w-8 items-center justify-center rounded-lg text-[var(--c-ink-3)] transition hover:bg-rose-500/15 hover:text-rose-700 dark:hover:text-rose-300"
                          aria-label="Remove"
                        >
                          ✕
                        </button>
                      </div>
                    </div>

                    {expanded && (
                      <div className="border-t border-[var(--c-line)] bg-[var(--c-sunken)] p-4">
                        <div className="mb-2.5 flex items-baseline justify-between">
                          <span className="text-sm font-semibold text-[var(--c-ink)]">Professions</span>
                          <span className={`text-xs font-semibold ${primaryCount >= maxPrimary ? "text-amber-700 dark:text-amber-300" : "text-[var(--c-ink-3)]"}`}>
                            {primaryCount} of {maxPrimary} main professions
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {primaries.map((p) => {
                            const on = combo.professions.includes(p.item_key);
                            const full = !on && primaryCount >= maxPrimary;
                            return (
                              <button
                                key={p.item_key}
                                type="button"
                                onClick={() => toggleProfession(i, p.item_key)}
                                disabled={full}
                                className={`rounded-full border px-3 py-1.5 text-[13px] font-medium transition ${
                                  on
                                    ? "border-emerald-400/60 bg-emerald-400/15 text-emerald-800 dark:text-emerald-100"
                                    : full
                                      ? "cursor-not-allowed border-[var(--c-line-soft)] text-[var(--c-ink-4)]"
                                      : "border-[var(--c-line)] bg-[var(--c-sunken)] text-[var(--c-ink-2)] hover:bg-[var(--c-sunken-2)]"
                                }`}
                              >
                                {on ? "✓ " : ""}{p.label}
                              </button>
                            );
                          })}
                        </div>
                        <div className="mb-2 mt-4 text-xs font-semibold uppercase tracking-wider text-[var(--c-ink-3)]">
                          Secondary
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {secondaries.map((p) => {
                            const on = combo.professions.includes(p.item_key);
                            return (
                              <button
                                key={p.item_key}
                                type="button"
                                onClick={() => toggleProfession(i, p.item_key)}
                                className={`rounded-full border px-3 py-1.5 text-[13px] font-medium transition ${
                                  on
                                    ? "border-sky-400/60 bg-sky-400/15 text-sky-800 dark:text-sky-100"
                                    : "border-[var(--c-line)] bg-[var(--c-sunken)] text-[var(--c-ink-2)] hover:bg-[var(--c-sunken-2)]"
                                }`}
                              >
                                {on ? "✓ " : ""}{p.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </Section>

        {/* 4. Note ----------------------------------------------------------- */}
        <Section step={4} title="Anything else?" subtitle="Optional. Raid experience, what you would rather avoid, anything.">
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={4}
            placeholder="Say whatever your officers should know."
            className="w-full resize-y rounded-2xl border border-[var(--c-line)] bg-[var(--c-surface)] px-4 py-3.5 text-[15px] leading-relaxed text-[var(--c-ink)] outline-none transition placeholder:text-[var(--c-ink-4)] focus:border-amber-400/60 focus:ring-4 focus:ring-amber-500/10"
          />
        </Section>
      </div>

      {/* Returning players skip the intro, so they see the note here. */}
      <p className="mt-8 whitespace-pre-line px-1 text-sm leading-relaxed text-[var(--c-ink-3)]">{signupNote}</p>

      {/* Sticky, so the one action on the page is always in reach, and it says
          what is still missing rather than just refusing to be pressed. */}
      <div className="sticky bottom-4 z-20 mt-4">
        <Glass className="flex flex-wrap items-center gap-3 p-3 sm:p-4">
          <div className="min-w-0 flex-1 px-1">
            {error ? (
              <span className="text-sm font-medium text-rose-700 dark:text-rose-300">{error}</span>
            ) : missing.length > 0 ? (
              <span className="text-sm text-[var(--c-ink-2)]">
                Still need <span className="font-semibold text-amber-800 dark:text-amber-200">{missing.join(", ")}</span>
              </span>
            ) : (
              <span className="text-sm text-[var(--c-ink-2)]">
                <span className="font-semibold text-[var(--c-ink)]">{displayName}</span>
                {" · "}
                {choiceLabel(catalog?.factions || [], faction)}
                {" · "}
                {choiceLabel(catalog?.realmTypes || [], realmType)}
                {" · "}
                {openToAnything && combos.length === 0
                  ? "officers choose"
                  : `${combos.length} ${combos.length === 1 ? "pick" : "picks"}`}
              </span>
            )}
          </div>
          <PrimaryButton onClick={submit} disabled={saving || missing.length > 0}>
            {saving ? "Saving" : "Submit"}
          </PrimaryButton>
        </Glass>
      </div>
    </>,
    sidebar
  );
}
